import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Client } from 'filestack-js/build/main/lib/client';
import Swal from 'sweetalert2';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useHistory } from 'react-router-dom';

import { formatPhoneNumber, isValidUrl } from 'utils';
import { OfferSettingsJsonType } from '@bb/domain/dist/types';
import {
  PickerOptions,
  PickerResponse,
  PickerInstance,
} from 'filestack-js/build/main/lib/picker';
import { ErrorType } from './types';

import ExternalOfferHeading from './ExternalOfferHeading';
import InformationPanel from './InformationPanel';
import RegisterOfferPanel from './RegisterOfferPanel';

declare const fileStackClient: Client;

export interface ExternalOfferProps {
  $routeParams: {
    retsId?: string;
    dashboardId?: string;
  };
  ExternalService: any;
  listingNotificationSettings: any;
  ExternalAuth: any;
  ErrorService: any;
}

export interface ExternalOfferState {
  dataLoaded: boolean;
  listing: any;
  notifications: {
    registeredOffers: boolean;
  };
  offerDetails: {
    offerSettings: OfferSettingsJsonType;
    offerSubmissions?: string;
    offerCount: number;
  };
  orgDetails: {
    name: string;
    phone: string;
  };
  error: ErrorType;
  recaptchaOpen: boolean;
  offer: {
    dashboardId: string;
    attachmentForm801: {
      filename: string;
      url: string;
    } | null;
    irrevocable?: string;
    orgId: string;
    phone: string;
  };
}

const ExternalOffer: React.FC<ExternalOfferProps> = props => {
  const {
    $routeParams: { retsId, dashboardId },
    ExternalService,
    listingNotificationSettings,
    ExternalAuth,
    ErrorService,
  } = props;

  const history = useHistory();

  const hasOffersFeature = ExternalAuth.auth.hasFeature('OFFERS');
  const [state, setState] = React.useState<ExternalOfferState>({
    dataLoaded: false,
    listing: {},
    offerDetails: {
      offerSettings: {} as OfferSettingsJsonType,
      offerSubmissions: '',
      offerCount: 0,
    },
    orgDetails: {
      name: '',
      phone: '',
    },
    notifications: {
      registeredOffers: false,
    },
    offer: {
      orgId: '',
      dashboardId: '',
      irrevocable: null,
      phone: '',
      attachmentForm801: null,
    },
    error: {
      form801: false,
      phone: false,
      irrevocable: false,
      pastDate: false,
    },
    recaptchaOpen: false,
  });
  const [pickerInstance, setPickerInstance] = useState<PickerInstance | null>(
    null,
  );

  const fetchDataRets = async () => {
    const results = await Promise.all([
      ExternalService.getOfferDetails(retsId),
      listingNotificationSettings.get(retsId),
    ]);
    const [offerDetails, notifications] = results;
    const { listing } = ExternalAuth.auth;
    const subBrokerage =
      listing.org.addresses?.[listing.location]?.[listing.address];
    const orgDetails = {
      name: subBrokerage?.name || listing.org.name,
      phone: subBrokerage?.phone || '',
    };

    return {
      listing,
      offerDetails,
      notifications,
      orgDetails,
    };
  };

  const fetchDataDash = async () => {
    const results = await Promise.all([
      ExternalService.getDashOfferDetails(dashboardId),
      listingNotificationSettings.getDash(dashboardId),
    ]);
    const [offerDetails, notifications] = results;
    const { listing } = ExternalAuth.auth;
    const subBrokerage =
      listing.org.addresses?.[listing.location]?.[listing.address];
    const orgDetails = {
      name: subBrokerage?.name || listing.org.name,
      phone: subBrokerage?.phone || '',
    };

    return {
      listing,
      offerDetails,
      notifications,
      orgDetails,
      offer: {
        ...state.offer,
        orgId: ExternalAuth.auth.listing.org,
        dashboardId,
        irrevocable: moment(
          listing?.status === 'Coming Soon'
            ? listing.coming_soon_date!
            : undefined,
        )
          .endOf('day')
          .toDate(),
      },
    };
  };

  React.useEffect(() => {
    const fetchData = async () => {
      let changes = {};
      try {
        if (retsId) {
          changes = await fetchDataRets();
        } else {
          changes = await fetchDataDash();
        }
      } catch (err) {
        ErrorService.toastyHTTPErr(err);
      } finally {
        setState({ ...state, ...changes, dataLoaded: true });
      }
    };
    if (hasOffersFeature) fetchData();
  }, []);

  useEffect(() => () => {
      if (pickerInstance && typeof pickerInstance.close === 'function')
        pickerInstance.close();
    }, [history, pickerInstance]);

  const canRegisterOffer = (): boolean => {
    const { offerSettings } = state.offerDetails;
    if (!offerSettings) return false;

    const { registration, acceptedAnytime, offerDate, preemptive } =
      offerSettings;

    if (
      !registration ||
      !registration.isEnabled ||
      !registration.shouldAllowMls
    ) {
      return false;
    }
    if (acceptedAnytime) return true;

    const startOfDay = new Date(offerDate);
    startOfDay.setMinutes(1);
    startOfDay.setHours(0);

    if (offerDate && startOfDay < new Date()) {
      return true;
    }

    if (preemptive) {
      return true;
    }

    return false;
  };

  const openFileUpload = () => {
    if (state.recaptchaOpen) return;

    const opts: PickerOptions = {
      maxFiles: 1,
      onFileSelected: file => {
        if (file.source === 'url' && !isValidUrl(file.originalPath))
          throw new Error('Invalid URL format. Please try again.');
      },
      onUploadDone: (result: PickerResponse) => {
        if (result.filesUploaded?.length) {
          const offer = {
            ...state.offer,
            attachmentForm801: result.filesUploaded[0],
          };
          const error = {
            ...state.error,
            form801: false,
          };
          setState({ ...state, offer, error });
        }
      },
      onClose: () => {
        setPickerInstance(null);
      },
    };
    const picker = fileStackClient.picker(opts);
    setPickerInstance(picker);
    picker.open();
  };

  const goBack = () => {
    if (retsId) {
      window.location.href = `/dashboard/#/rets/${retsId}/appointments/book`;
    } else {
      window.location.href = `/dashboard/#/dash/${dashboardId}/appointments`;
    }
  };

  const submit = (captchaResponse?: string, captchaV3Token?: string) => {
    const offer = { ...state.offer, captchaResponse, captchaV3Token };
    const { offerSettings } = state.offerDetails;

    const doSubmit = () => {
      setState({ ...state, dataLoaded: false });

      let promises;
      if (retsId) {
        promises = [
          ExternalService.registerOffer({ ...offer, retsId }),
          listingNotificationSettings.save(retsId, state.notifications),
        ];
      } else {
        promises = [
          ExternalService.registerDashOffer(dashboardId, offer),
          listingNotificationSettings.saveDash(
            dashboardId,
            state.notifications,
          ),
        ];
      }

      let showChallenge = false;
      Promise.all(promises)
        .then(([res]) => {
          ({ showChallenge } = res);
          if (showChallenge) {
            return setState({
              ...state,
              recaptchaOpen: true,
              dataLoaded: true,
            });
          }
          Swal.fire({
            title: 'Offer Registered!',
            type: 'success',
          }).then(() => {
            goBack();
          });
        })
        .catch(ErrorService.toastyHTTPErr)
        .finally(() => {
          if (state.recaptchaOpen)
            setState({
              ...state,
              dataLoaded: true,
              offer,
            });
        });
    };

    if (
      offerSettings.registration.shouldSubmitOfferDoc &&
      !state.recaptchaOpen
    ) {
      Swal.fire({
        title: 'VALID OFFER DOC',
        text: 'By clicking "Register My Offer", you verify that the file you have attached satisfies the document requirements for offer registration set by your real estate board.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Register My Offer',
        cancelButtonText: 'Cancel',
      }).then(({ value }) => {
        if (value) {
          doSubmit();
        } else {
          setState({ ...state, recaptchaOpen: false, offer });
        }
      });
    } else {
      doSubmit();
    }
  };

  const validate = () => {
    const error: any = {};
    let hasError = false;

    if (!state.offer.phone) {
      error.phone = true;
      hasError = true;
    }

    if (
      state.offerDetails.offerSettings.registration.shouldSubmitOfferDoc &&
      !state.offer.attachmentForm801
    ) {
      error.form801 = true;
      hasError = true;
    }

    if (!state.offer.irrevocable) {
      error.irrevocable = true;
      hasError = true;
    } else {
      const irrevocableDate = new Date(state.offer.irrevocable);
      if (Number.isNaN(irrevocableDate.getTime())) {
        error.invalidDate = true;
        hasError = true;
      } else if (irrevocableDate < new Date()) {
        error.pastDate = true;
        hasError = true;
      } else if (
        state.listing.status === 'Coming Soon' &&
        irrevocableDate <
          moment(state.listing.coming_soon_date).startOf('day').toDate()
      ) {
        error.beforeCsDate = true;
        hasError = true;
      }
    }

    if (hasError) {
      setState({ ...state, error });
    }

    return hasError;
  };

  const handleDateChange = (
    changes: Partial<ExternalOfferState['offer']>,
    errorName?: keyof ErrorType,
  ) => {
    const errors: ErrorType = {
      invalidDate: false,
      pastDate: false,
      beforeCsDate: false,
    };
    if (changes.irrevocable && !moment(changes.irrevocable).isValid()) {
      errors.invalidDate = true;
    } else if (
      changes.irrevocable &&
      moment(changes.irrevocable).toDate() < new Date()
    ) {
      errors.pastDate = true;
    } else if (
      state.listing.status === 'Coming Soon' &&
      moment(changes.irrevocable).toDate() <
        moment(state.listing.coming_soon_date).startOf('day').toDate()
    ) {
      errors.beforeCsDate = true;
    }

    setState({
      ...state,
      offer: { ...state.offer, ...changes },
      error: {
        ...state.error,
        ...(errorName && { [errorName]: false }),
        ...errors,
      },
    });
  };

  if (!state.dataLoaded) return null;

  if (!state.listing.id) {
    return (
      <div
        className="normalheader transition animated fadeIn external-offer"
        data-cy="offer-external-module"
      >
        <div className="hpanel">
          <div className="panel-body error-panel">
            <h3 className="font-light m-b-xs">
              You are not able to register an offer online at this time.
              <br />
              For more information, please call the listing brokerage:
            </h3>
            <div className="error-panel-org">
              <p>{state.orgDetails.name}</p>
              <p>{formatPhoneNumber(state.orgDetails.phone)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (
    !state.offerDetails?.offerSettings?.registration?.isEnabled ||
    !state.offerDetails?.offerSettings?.registration?.shouldAllowMls
  ) {
    const {
      org: { addresses },
      location,
      address,
      reps: [rep],
    } = state.listing;
    const addressDetails = addresses[location] && addresses[location][address];
    const { phone, name } = addressDetails || {};

    return (
      <div className="normalheader transition animated fadeIn external-offer">
        <div className="hpanel">
          <div className="panel-body">
            <p>
              Online Offer registration has been disabled for this property.
              <br />
              To register an offer, please call the Listing Brokerage.
            </p>
            <h3>Contact Information:</h3>
            <p>
              {rep.firstName} {rep.lastName}
            </p>
            <p>{name || state.orgDetails.name}</p>
            <p>
              Office Phone: {formatPhoneNumber(phone || state.orgDetails.phone)}
            </p>
            {rep?.frontEndInstructions?.releaseCell && (
              <p>Agent Mobile: {formatPhoneNumber(rep.phone)}</p>
            )}
            <p>
              Agent Email: <a href={`mailto:${rep.email}`}>{rep.email}</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state.offerDetails?.offerSettings) {
    let { acceptedAnytime } = state.offerDetails.offerSettings;
    if (typeof acceptedAnytime === 'string') {
      acceptedAnytime = acceptedAnytime === 'true';
    }

    return (
      <GoogleReCaptchaProvider
        useEnterprise
        reCaptchaKey={process.env.RECAPTCHA_V3_SITE_KEY}
      >
        <div className="external-offer">
          <div className="normalheader transition animated fadeIn">
            <ExternalOfferHeading
              state={state}
              setState={setState}
              canRegisterOffer={canRegisterOffer}
              submit={submit}
              validate={validate}
            />
          </div>
          <div className="content transition animated fadeIn">
            <div className="row">
              <InformationPanel
                acceptedAnytime={acceptedAnytime}
                offerSubmissions={state.offerDetails.offerSubmissions}
                shouldShowOfferCount={
                  state.offerDetails.offerSettings.shouldShowOfferCount
                }
                offerCount={state.offerDetails.offerCount}
                offerDate={state.offerDetails.offerSettings.offerDate}
                requestIrrevocable={
                  state.offerDetails.offerSettings.requestIrrevocable
                }
                otherInstructions={
                  state.offerDetails.offerSettings.otherInstructions
                }
              />
              <RegisterOfferPanel
                canRegisterOffer={canRegisterOffer}
                user={ExternalAuth.auth.user}
                offerDate={state.offerDetails.offerSettings.offerDate}
                irrevocable={state.offer.irrevocable}
                phone={state.offer.phone}
                shouldSubmitOfferDoc={
                  state.offerDetails.offerSettings.registration
                    .shouldSubmitOfferDoc
                }
                attachmentForm801={state.offer.attachmentForm801}
                recaptchaOpen={state.recaptchaOpen}
                openFileUpload={openFileUpload}
                handleChange={handleDateChange}
                error={state.error}
                listing={state.listing}
              />
            </div>
          </div>
        </div>
      </GoogleReCaptchaProvider>
    );
  }

  return null;
};

export default ExternalOffer;
